/**
 * InvoiceCreatePage — full-page invoice creation form (iter-156).
 *
 * Route: /dashboard/finance/create-invoice/:platform
 *
 * Highlights vs. the previous version:
 *  • Live email + UAE-style phone validation
 *  • Service Type is a proper <select> dropdown with an "Other — type manually"
 *    tail option that reveals a text field
 *  • Form order: Customer → Payment Method → Services → Totals → Notes
 *  • Tabby / Tamara commission preview + optional override (only allowed >=
 *    the settings minimum; enforced backend-side too)
 *  • Cash / Bank / Check / Credit Card:
 *      – "Payment Status" radio: Unpaid | Paid | Partially Paid
 *      – Paid → mandatory receipt upload for the FULL total
 *      – Partial → mandatory {amount + method + receipt} for the first payment
 *  • Uploaded receipts run through Gemini OCR (best-effort) to auto-fill the
 *    amount + reference fields — the operator can always override.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Plus, Trash2, Loader2, Search, ExternalLink, Copy, CheckCircle2,
  Info, ArrowLeft, ListChecks, Sparkles, Upload, FileImage, Lock,
} from 'lucide-react';

import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { TrnBadge } from '../../components/TrnBadge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { floorAED } from '../../utils/money';
import { FINANCE_PLATFORM_META } from '../../constants/financePlatforms';
import { DirhamPrice } from '../../components/DirhamPrice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Payment methods — "payment_link" intentionally omitted (Stripe/Ziina/etc.
// already ARE payment-link providers).
const PAYMENT_METHOD_OPTIONS = [
  { id: 'cash',          ar: 'نقدي',         en: 'Cash',          group: 'manual' },
  { id: 'bank_transfer', ar: 'تحويل بنكي',   en: 'Bank Transfer', group: 'manual' },
  { id: 'check',         ar: 'شيك',          en: 'Check',         group: 'manual' },
  { id: 'credit_card',   ar: 'بطاقة ائتمان', en: 'Credit Card',   group: 'manual' },
  { id: 'tamara',        ar: 'تمارا',        en: 'Tamara',        group: 'gateway' },
  { id: 'tabby',         ar: 'تابي',         en: 'Tabby',         group: 'gateway' },
  { id: 'stripe',        ar: 'ستريب',        en: 'Stripe',        group: 'gateway' },
  { id: 'ziina',         ar: 'زينة',         en: 'Ziina',         group: 'gateway' },
];

const MANUAL_METHODS = new Set(['cash', 'bank_transfer', 'check', 'credit_card']);

const OTHER_SERVICE_ID = '__other__';

// iter-278 (Feb 2026) — Map finance-platform ids to invoice-catalog platform ids.
// The invoice service catalog (`/api/admin/service-catalog/invoice`) is keyed
// by the canonical INVOICE_PLATFORMS list from admin_service_catalogs.py.
// Empty means "shared / no filter" (use all rows).
const INVOICE_CATALOG_PLATFORM_MAP = {
  cloudgm_customers: 'cloudgm',
  cloudgm_merchants: 'cloudgm_business',
  agents:            'cloudgm_business_agents',
  fitness:           'fitness_606',
  cgm_coin:          'cgm_coin',
  community:         'community_606',
  mediadocs:         'mediadocs_606',
};

// ── Validation helpers ────────────────────────────────────────────
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
// Accepts +971XXXXXXXXX (9 digits after +971), or 0XXXXXXXXX, or 971XXXXXXXXX,
// or any valid international format 8–15 digits with optional +.
const PHONE_RE = /^(?:\+?\d{8,15}|0\d{8,10})$/;

const cleanPhone = (v) => (v || '').replace(/[\s\-()]/g, '');

// Route mapping — where the "back to list" button navigates for each platform
const listRouteFor = (platform) => {
  if (platform === 'cloudgm_customers') return '/dashboard/finance/cloudgm/customers/payments';
  if (platform === 'cloudgm_merchants') return '/dashboard/finance/cloudgm/merchants/payments';
  if (platform === 'agents')            return '/dashboard/finance/cloudgm/merchants/agent-payments';
  return `/dashboard/finance/pcol/${platform}`;
};

const hubRouteFor = (platform) => {
  if (platform === 'cloudgm_customers') return '/dashboard/finance/cloudgm/customers';
  if (platform === 'cloudgm_merchants') return '/dashboard/finance/cloudgm/merchants';
  if (platform === 'agents')            return '/dashboard/finance/cloudgm/merchants';
  return `/dashboard/finance/platform/${platform}`;
};

const emptyForm = () => ({
  customer_id: null,
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  payment_method: '',
  notes: '',
  // Commission override for Tabby / Tamara
  commission_override_value: '',
  // Payment status for manual methods
  initial_payment_status: 'unpaid',
  // iter-224 (Feb 2026) — Supporting attachments (internal-only). Each item:
  //   { file_id, filename, size, uploaded_at }
  supporting_attachments: [],
  // iter-278 (Feb 2026) — Receipts for paid_invoice status. Each item:
  //   { file_id, filename, amount, method, reference, ai_verified }
  paid_receipts: [],
});

const emptyServiceRow = () => ({
  service_type_id: '',   // dropdown id from serviceTypes (or OTHER_SERVICE_ID)
  service_type_ar: '',
  service_type_en: '',
  // iter-218n (Feb 2026) — CloudGM Business invoices use Main + Sub picker
  service_main_id: '',
  service_sub_id: '',
  description: '',
  amount: '',
});

const emptyInitialPayment = () => ({
  method: 'cash',       // for partial payment: which method the first slice used
  amount: '',
  proof: null,          // { file_id, filename } after upload
  note: '',
});

export default function InvoiceCreatePage() {
  const { language } = useLanguage();
  const { platform } = useParams();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const platMeta = FINANCE_PLATFORM_META[platform];
  const invoiceListRoute = listRouteFor(platform);
  const parentHubRoute = hubRouteFor(platform);

  // ── Reference data ──────────────────────────────────────────────
  const [tax, setTax] = useState({ tax_percent: 0, enabled: false });
  const [serviceTypes, setServiceTypes] = useState([]);
  // iter-218n (Feb 2026) — Cloudgm_merchants invoices switch to a
  // "Main service + Sub service" picker sourced from the shared
  // service-catalog (same list used by the manual agent-task form).
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // ── Form state ──────────────────────────────────────────────────
  const [form, setForm] = useState(emptyForm());
  const [services, setServices] = useState([emptyServiceRow()]);
  const [initialPayment, setInitialPayment] = useState(emptyInitialPayment());

  // Customer search
  const [searchEnabled, setSearchEnabled] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // ── Loaders ─────────────────────────────────────────────────────
  const loadTax = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/tax-settings`);
      setTax(r.data.data || {});
    } catch { /* ignore */ }
  }, []);

  const loadServiceTypes = useCallback(async () => {
    if (!platform) return;
    try {
      const r = await axios.get(`${API_URL}/api/admin/dropdown-lists`, {
        headers: getAuthHeaders(),
        params: { list_type: 'invoice_service_type', platform },
      });
      setServiceTypes(r.data.data || []);
    } catch { /* ignore */ }
  }, [platform]);

  // iter-278 (Feb 2026) — All finance platforms now use the shared
  // `invoice` service catalog, filtered by the mapped invoice platform id.
  // The picker falls back to the flat serviceTypes dropdown when empty.
  const loadServiceCatalog = useCallback(async () => {
    if (!platform) return;
    const catPlatform = INVOICE_CATALOG_PLATFORM_MAP[platform];
    if (!catPlatform) return;
    try {
      const r = await axios.get(`${API_URL}/api/admin/service-catalog/invoice`, {
        headers: getAuthHeaders(),
        params: { platform: catPlatform },
      });
      setServiceCatalog(r.data.data || []);
    } catch { /* ignore */ }
  }, [platform]);

  const loadPaymentMethods = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/payment-methods`, { headers: getAuthHeaders() });
      setPaymentMethods(r.data.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadTax(); loadServiceTypes(); loadServiceCatalog(); loadPaymentMethods(); },
           [loadTax, loadServiceTypes, loadServiceCatalog, loadPaymentMethods]);

  // ── Customer search debounce ────────────────────────────────────
  useEffect(() => {
    if (!searchEnabled || !searchQ.trim() || searchQ.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API_URL}/api/admin/invoices/customers/search`, {
          headers: getAuthHeaders(),
          params: { platform, q: searchQ.trim() },
        });
        setSearchResults(r.data.data || []);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchEnabled, searchQ, platform]);

  // ── Derived values ──────────────────────────────────────────────
  const subtotal = useMemo(
    () => services.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0),
    [services]
  );
  const taxAmount = tax.enabled ? floorAED(subtotal * (tax.tax_percent || 0) / 100) : 0;
  const totalBeforeCommission = floorAED(subtotal + taxAmount);

  const chosenMethod = PAYMENT_METHOD_OPTIONS.find(m => m.id === form.payment_method);
  const isGatewayMethod = chosenMethod?.group === 'gateway';
  const isManualMethod = MANUAL_METHODS.has(form.payment_method);

  // Gateway readiness
  const providerReadiness = useMemo(() => {
    const map = {};
    paymentMethods.forEach(m => {
      map[m.provider] = {
        ready: m.ready,
        enabled_here: (m.platforms || []).includes(platform) && m.enabled,
        env: m.env,
        // commission fields for tabby / tamara
        fee_enabled: !!m.fee_enabled,
        fee_type: m.fee_type || 'percentage',
        fee_percent: Number(m.fee_percent || 0),
        fee_fixed_amount: Number(m.fee_fixed_amount || 0),
        display_name: m.display_name,
        display_name_ar: m.display_name_ar,
      };
    });
    return map;
  }, [paymentMethods, platform]);

  const providerToUse = isGatewayMethod ? form.payment_method : null;
  const providerIssue = providerToUse
    ? (!providerReadiness[providerToUse]?.enabled_here
        ? t('هذه البوابة مُعطّلة على هذه المنصة (من إعدادات بوابات الدفع)',
             'This gateway is disabled on this platform (see Payment Gateways Settings)')
        : (!providerReadiness[providerToUse]?.ready
            ? t('هذه البوابة لم تُضبط مفاتيحها بعد',
                 'This gateway is not configured yet (missing API keys)')
            : null))
    : null;

  // Commission: only when method is tabby OR tamara AND fee_enabled in registry
  const commissionCfg = useMemo(() => {
    if (!['tabby', 'tamara'].includes(form.payment_method)) return null;
    const cfg = providerReadiness[form.payment_method];
    if (!cfg || !cfg.fee_enabled) return null;
    const type = cfg.fee_type;
    const min = type === 'percentage' ? cfg.fee_percent : cfg.fee_fixed_amount;
    const overrideNum = parseFloat(form.commission_override_value);
    const chosen = !isNaN(overrideNum) ? overrideNum : min;
    const belowMin = !isNaN(overrideNum) && overrideNum + 1e-6 < min;
    const commissionAmount = type === 'percentage'
      ? floorAED(subtotal * chosen / 100)
      : floorAED(chosen);
    return { type, min, chosen, belowMin, amount: commissionAmount };
  }, [form.payment_method, form.commission_override_value, providerReadiness, subtotal]);

  const total = floorAED(totalBeforeCommission + (commissionCfg?.amount || 0));

  // ── Field validation ────────────────────────────────────────────
  const emailValid = !form.customer_email || EMAIL_RE.test(form.customer_email.trim());
  const phoneValid = !form.customer_phone || PHONE_RE.test(cleanPhone(form.customer_phone));

  // Initial-payment validation
  const remainingAfterFirst = useMemo(() => {
    const amt = parseFloat(initialPayment.amount);
    if (isNaN(amt)) return total;
    return floorAED(total - amt);
  }, [initialPayment.amount, total]);

  const initialPaymentValid = useMemo(() => {
    // iter-278 — paid_invoice status validates paid_receipts (see below).
    if (form.initial_payment_status === 'paid_invoice') return true;
    if (!isManualMethod) return true;
    if (form.initial_payment_status === 'unpaid') return true;
    // Paid or partially_paid → must have proof + amount
    if (!initialPayment.proof?.file_id) return false;
    const amt = parseFloat(initialPayment.amount);
    if (isNaN(amt) || amt <= 0) return false;
    if (form.initial_payment_status === 'paid') return amt + 0.01 >= total;
    // partially_paid → amount must be > 0 AND < total
    return amt > 0 && amt + 0.01 < total;
  }, [isManualMethod, form.initial_payment_status, initialPayment, total]);

  // iter-278 — paid_invoice-specific validation: receipts must sum to >= total AND be AI-verified.
  const paidReceiptsSum = useMemo(
    () => (form.paid_receipts || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
    [form.paid_receipts]
  );
  const paidReceiptsValid = useMemo(() => {
    if (form.initial_payment_status !== 'paid_invoice') return true;
    const list = form.paid_receipts || [];
    if (list.length < 1) return false;
    // Every row must have file_id + amount > 0 + AI-verified flag set.
    if (!list.every(r => r.file_id && parseFloat(r.amount) > 0 && r.ai_verified)) return false;
    return paidReceiptsSum + 0.01 >= total;
  }, [form.initial_payment_status, form.paid_receipts, paidReceiptsSum, total]);

  const canSubmit =
    form.customer_name.trim() &&
    emailValid && phoneValid &&
    // iter-218n (Feb 2026) — Payment method only required when status !== unpaid.
    // Unpaid invoices are saved with an empty payment_method; the "convert to
    // paid" button on the invoice detail page will pick a method later.
    // iter-278 — paid_invoice does not need a payment_method either (multi-receipt).
    (form.initial_payment_status === 'unpaid' ||
     form.initial_payment_status === 'paid_invoice' ||
     form.payment_method) &&
    services.length > 0 &&
    services.every(s => (s.service_type_ar || s.service_type_en) && parseFloat(s.amount) > 0) &&
    !providerIssue &&
    !(commissionCfg?.belowMin) &&
    initialPaymentValid &&
    paidReceiptsValid;

  // ── Submit ──────────────────────────────────────────────────────
  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        platform,
        customer_id: form.customer_id,
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim() || null,
        customer_phone: cleanPhone(form.customer_phone) || null,
        services: services.map(s => ({
          service_type_ar: s.service_type_ar.trim(),
          service_type_en: s.service_type_en.trim(),
          // iter-218n — Persist the catalog IDs so future reports can
          // group by main/sub even after the display strings are edited.
          service_main_id: s.service_main_id || null,
          service_sub_id: s.service_sub_id || null,
          description: s.description.trim(),
          amount: parseFloat(s.amount),
        })),
        payment_method: (form.initial_payment_status === 'unpaid' || form.initial_payment_status === 'paid_invoice')
                          ? null
                          : (form.payment_method || null),
        // iter-218n — Persist the initial payment status even for unpaid so
        // the invoice list can render the correct badge and the "convert
        // to paid" button can appear on the detail page.
        initial_payment_status: form.initial_payment_status,
        // iter-224 (Feb 2026) — Supporting attachments (never surfaced on
        // customer PDF/link — kept internal for reference).
        supporting_attachments: (form.supporting_attachments || []).map(a => ({
          file_id: a.file_id, filename: a.filename, size: a.size,
        })),
        // iter-278 — Paid_invoice receipts (each AI-verified against total).
        paid_receipts: form.initial_payment_status === 'paid_invoice'
          ? (form.paid_receipts || []).map(r => ({
              file_id: r.file_id,
              filename: r.filename,
              amount: parseFloat(r.amount),
              method: r.method || 'bank_transfer',
              reference: r.reference || null,
              ai_verified: !!r.ai_verified,
              note: r.note || '',
            }))
          : [],
        notes: form.notes.trim(),
      };

      // Attach commission override if applicable
      if (commissionCfg) {
        body.commission_type = commissionCfg.type;
        body.commission_value = commissionCfg.chosen;
      }

      // Attach initial payment for manual methods
      if (isManualMethod && form.initial_payment_status !== 'unpaid') {
        body.initial_payment_status = form.initial_payment_status;
        body.initial_payment = {
          amount: parseFloat(initialPayment.amount),
          method: initialPayment.method,
          proof_file_id: initialPayment.proof?.file_id,
          proof_filename: initialPayment.proof?.filename,
          note: initialPayment.note.trim(),
        };
      }

      const r = await axios.post(`${API_URL}/api/admin/invoices`, body, { headers: getAuthHeaders() });
      setResult(r.data);
      toast.success(t('✓ تم إنشاء الفاتورة', '✓ Invoice created'));
      window.scrollTo(0, 0);
    } catch (e) {
      // Robust error extraction — pydantic errors come as an array of objects
      // and if we pass that directly to toast.error(), sonner tries to render
      // the objects which crashes React and blanks the whole page.
      const raw = e?.response?.data?.detail;
      let msg;
      if (typeof raw === 'string') msg = raw;
      else if (Array.isArray(raw)) msg = raw.map(x => x?.msg || JSON.stringify(x)).join(' · ');
      else if (raw) msg = JSON.stringify(raw);
      else msg = t('فشل الإنشاء', 'Create failed');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    if (!result?.checkout_url) return;
    navigator.clipboard.writeText(result.checkout_url).then(() => toast.success(t('تم النسخ', 'Copied')));
  };

  const resetForm = () => {
    setForm(emptyForm());
    setServices([emptyServiceRow()]);
    setInitialPayment(emptyInitialPayment());
    setResult(null);
    setSearchEnabled(false);
    setSearchQ('');
    setSearchResults([]);
    window.scrollTo(0, 0);
  };

  // ── Service line helpers ────────────────────────────────────────
  const addService = () => setServices(s => [...s, emptyServiceRow()]);
  const removeService = (idx) => setServices(s => s.length > 1 ? s.filter((_, i) => i !== idx) : s);
  const updateService = (idx, patch) => setServices(s => s.map((row, i) => i === idx ? { ...row, ...patch } : row));

  const pickCustomer = (u) => {
    setForm(f => ({
      ...f,
      customer_id: u.id,
      customer_name: u.name || '',
      customer_email: u.email || '',
      customer_phone: u.phone || '',
    }));
    setSearchResults([]);
    setSearchQ(u.name || u.email || '');
  };

  // Reset initial payment fields when payment_method or status changes
  useEffect(() => {
    if (!isManualMethod || form.initial_payment_status === 'unpaid') {
      setInitialPayment(emptyInitialPayment());
    } else if (form.initial_payment_status === 'paid') {
      // Autofill amount to the total
      setInitialPayment(p => ({ ...p, amount: String(total || ''), method: form.payment_method }));
    } else if (form.initial_payment_status === 'partially_paid') {
      setInitialPayment(p => ({ ...p, method: form.payment_method }));
    }
  }, [form.initial_payment_status, form.payment_method, isManualMethod, total]);

  if (!platMeta) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <p className="text-white/60">{t('منصة غير معروفة', 'Unknown platform')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] relative" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full blur-[150px]"
             style={{ backgroundColor: `${platMeta.color}0d` }} />
      </div>

      <DashboardHeader showBackButton backPath={parentHubRoute} />

      <div className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية',      label_en: 'Finance',   path: '/dashboard/finance' },
            { label_ar: isAr ? platMeta.ar : platMeta.en, label_en: platMeta.en, path: parentHubRoute },
            // iter-261 (Feb 2026) — Agent-invoice creation has its own label.
            {
              label_ar: platform === 'agents' ? 'إنشاء فاتورة للوكلاء'
                      : platform === 'cloudgm_merchants' ? 'إنشاء فاتورة لمنصة التجار عام'
                      : 'إنشاء فاتورة',
              label_en: platform === 'agents' ? 'Create Agent Invoice'
                      : platform === 'cloudgm_merchants' ? 'Create Invoice — Merchant Platform (General)'
                      : 'Create Invoice',
              path: null,
            },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: platMeta.color }} />
              <span className="text-white/50 text-sm">{isAr ? platMeta.ar : platMeta.en}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {platform === 'agents'
                ? t('إنشاء فاتورة للوكلاء', 'Create Agent Invoice')
                : platform === 'cloudgm_merchants'
                ? t('إنشاء فاتورة لمنصة التجار عام', 'Create Invoice — Merchant Platform (General)')
                : t('إنشاء فاتورة', 'Create Invoice')}
            </h1>
            <p className="text-white/50 text-sm">
              {platform === 'agents'
                ? t('ابحث عن وكيل مسجل، اختر الخدمة الفرعية، وأصدر الفاتورة. تنعكس الفواتير في "سجل مدفوعات فواتير الوكلاء" فقط.',
                    'Search a registered agent, pick the sub-service, and issue the invoice. Records reflect ONLY in the "Agent Invoice Payment Records" ledger.')
                : t('اختر العميل وطريقة الدفع، ثم أضف الخدمات وحدد حالة الفاتورة لإنشائها',
                    'Choose the customer and payment method, then add services and payment status')}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate(invoiceListRoute)}
            className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
            data-testid="invcreate-back-to-list"
          >
            <ListChecks className="w-4 h-4 me-2" />
            {platform === 'agents'
              ? t('سجل مدفوعات الوكلاء', 'Agent payment records')
              : t('سجل المدفوعات', 'Payment records')}
          </Button>
        </div>

        {/* iter-277 — Live TRN chip pulled from the tax certificate.
           Every invoice snapshots this at issuance so historic invoices
           remain stable. Renders nothing if the certificate is missing. */}
        <div className="mb-6">
          <TrnBadge language={isAr ? 'ar' : 'en'} testId="invcreate-trn-badge" />
        </div>

        {result ? (
          <ResultView
            result={result}
            copyLink={copyLink}
            onCreateAnother={resetForm}
            onDone={() => navigate(invoiceListRoute)}
            t={t} isAr={isAr}
          />
        ) : (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 space-y-6">
            {/* ─── 1. Customer ─── */}
            <section>
              <h3 className="text-white font-bold text-sm mb-3">{t('١) معلومات العميل', '1) Customer info')}</h3>
              <label className="inline-flex items-center gap-2 text-white/70 text-sm mb-3 cursor-pointer">
                <input type="checkbox" checked={searchEnabled}
                       onChange={(e) => setSearchEnabled(e.target.checked)}
                       className="accent-[#d4af37]"
                       data-testid="invcreate-customer-search-enable" />
                {t('البحث عن عميل مسجّل في هذه المنصة', 'Search for a registered customer on this platform')}
              </label>

              {searchEnabled && (
                <div className="mb-3 relative">
                  <div className="relative">
                    <Search className="absolute inset-y-0 start-3 my-auto w-4 h-4 text-white/40" />
                    <Input
                      value={searchQ}
                      onChange={(e) => setSearchQ(e.target.value)}
                      placeholder={t('اسم / بريد / هاتف', 'Name / email / phone')}
                      className="bg-[#050505] border-white/10 text-white ps-9"
                      data-testid="invcreate-customer-search-input"
                    />
                  </div>
                  {searching && <div className="text-white/40 text-xs mt-1">{t('جارٍ البحث…', 'Searching…')}</div>}
                  {searchResults.length > 0 && (
                    <div className="absolute z-20 top-full inset-x-0 mt-1 rounded-lg bg-[#0f0f0f] border border-white/10 max-h-56 overflow-y-auto shadow-2xl">
                      {searchResults.map((u) => (
                        <button key={u.id} onClick={() => pickCustomer(u)}
                                className="w-full text-start px-3 py-2 hover:bg-white/5 text-sm border-b border-white/5 last:border-0"
                                data-testid={`invcreate-customer-result-${u.id}`}>
                          <div className="text-white">{u.name || u.email}</div>
                          {u.email && <div className="text-white/40 text-xs">{u.email}</div>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label={t('اسم العميل', 'Customer name') + ' *'}>
                  <Input value={form.customer_name}
                         onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value, customer_id: null }))}
                         className="bg-[#050505] border-white/10 text-white"
                         data-testid="invcreate-customer-name" />
                </Field>
                <Field label={t('بريد العميل', 'Customer email')}
                       error={!emailValid ? t('بريد إلكتروني غير صالح', 'Invalid email format') : null}>
                  <Input type="email" value={form.customer_email}
                         onChange={(e) => setForm(f => ({ ...f, customer_email: e.target.value }))}
                         className={`bg-[#050505] text-white ${!emailValid ? 'border-red-500/60' : 'border-white/10'}`}
                         dir="ltr"
                         placeholder="name@example.com"
                         data-testid="invcreate-customer-email" />
                </Field>
                <Field label={t('هاتف العميل', 'Customer phone')}
                       error={!phoneValid ? t('رقم هاتف غير صالح', 'Invalid phone number') : null}>
                  <Input value={form.customer_phone}
                         onChange={(e) => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                         className={`bg-[#050505] text-white ${!phoneValid ? 'border-red-500/60' : 'border-white/10'}`}
                         dir="ltr"
                         placeholder="+971 50 123 4567"
                         data-testid="invcreate-customer-phone" />
                </Field>
              </div>
            </section>

            {/* ─── 2. Payment Status — iter-218n: moved BEFORE payment method.
                     Choosing "unpaid" hides the payment method block entirely
                     and the invoice can be saved without picking one; the
                     detail page will surface a "convert to paid" button
                     later that opens the picker. */}
            <section>
              <h3 className="text-white font-bold text-sm mb-3">
                {t('٢) حالة الفاتورة', '2) Invoice status')} *
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { id: 'unpaid',         ar: 'فاتورة غير مدفوعة', en: 'Unpaid invoice',  tone: 'bg-amber-500/15 border-amber-500 text-amber-200' },
                  { id: 'paid',           ar: 'رابط دفع',           en: 'Payment link',    tone: 'bg-emerald-500/15 border-emerald-500 text-emerald-200' },
                  { id: 'partially_paid', ar: 'مدفوع جزئي',         en: 'Partially paid',  tone: 'bg-blue-500/15 border-blue-500 text-blue-200' },
                  { id: 'paid_invoice',   ar: 'فاتورة مدفوعة',      en: 'Paid invoice',    tone: 'bg-purple-500/15 border-purple-500 text-purple-200' },
                ].map((s) => {
                  const active = form.initial_payment_status === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setForm(f => ({
                        ...f,
                        initial_payment_status: s.id,
                        // Reset payment_method when switching to unpaid so
                        // the hidden picker doesn't leak stale state.
                        payment_method: s.id === 'unpaid' ? '' : f.payment_method,
                      }))}
                      className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                        active
                          ? s.tone + ' shadow-sm'
                          : 'bg-[#050505] border-white/10 text-white/70 hover:border-white/30'
                      }`}
                      data-testid={`invcreate-status-${s.id}`}
                    >
                      {isAr ? s.ar : s.en}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 text-[11px] text-white/40">
                {form.initial_payment_status === 'unpaid'
                  ? t('الفاتورة ستُحفظ كغير مدفوعة — يمكنك تحويلها لرابط دفع أو مدفوع جزئي لاحقاً من صفحة الفاتورة.',
                       'Invoice will be saved as unpaid — you can convert it to a payment link or partially paid later from the invoice detail page.')
                  : form.initial_payment_status === 'paid'
                    ? t('اختر طريقة الدفع أدناه — سيتم إنشاء رابط دفع أو تسجيل دفعة كاملة.',
                         'Pick a payment method below — a payment link or full payment record will be created.')
                    : form.initial_payment_status === 'paid_invoice'
                      ? t('العميل دفع مسبقاً — ارفع إيصالات الدفع وسيقوم الذكاء الاصطناعي بالتحقق من مطابقة المبالغ.',
                           'Customer already paid — upload the receipts and AI will verify the amounts match the total.')
                      : t('اختر طريقة الدفع أدناه.', 'Pick a payment method below.')}
              </div>
            </section>

            {/* ─── 3. Payment Method — hidden when status = unpaid OR paid_invoice ─── */}
            {form.initial_payment_status !== 'unpaid' &&
             form.initial_payment_status !== 'paid_invoice' && (
            <section>
              <h3 className="text-white font-bold text-sm mb-3">{t('٣) طريقة الدفع', '3) Payment method') + ' *'}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {PAYMENT_METHOD_OPTIONS.map((m) => {
                  const active = form.payment_method === m.id;
                  return (
                    <button key={m.id}
                            onClick={() => setForm(f => ({
                              ...f, payment_method: m.id,
                              commission_override_value: '',
                            }))}
                            className={`px-3 py-2.5 rounded-lg border text-sm transition-colors text-start ${
                              active
                                ? 'bg-[#d4af37]/10 border-[#d4af37] text-white font-medium'
                                : 'bg-[#050505] border-white/10 text-white/70 hover:border-white/30'
                            }`}
                            data-testid={`invcreate-method-${m.id}`}>
                      {isAr ? m.ar : m.en}
                    </button>
                  );
                })}
              </div>

              {providerIssue && (
                <div className="mt-2 text-xs text-amber-400">{providerIssue}</div>
              )}
              {isGatewayMethod && providerToUse && !providerIssue && (
                <div className="mt-2 text-xs text-emerald-400">
                  {t('سيتم إنشاء رابط دفع تلقائياً عند حفظ الفاتورة',
                     'A payment link will be created automatically when the invoice is saved')}
                </div>
              )}

              {/* Tabby / Tamara commission preview + override */}
              {commissionCfg && (
                <CommissionEditor
                  cfg={commissionCfg}
                  provider={form.payment_method}
                  overrideValue={form.commission_override_value}
                  onOverrideChange={(v) => setForm(f => ({ ...f, commission_override_value: v }))}
                  subtotal={subtotal}
                  t={t} isAr={isAr}
                />
              )}
            </section>
            )}

            {/* ─── 3. Services ─── */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-bold text-sm">{t('٣) تفاصيل الخدمة', '3) Service details')}</h3>
                <Button onClick={addService} size="sm" variant="outline"
                        className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
                        data-testid="invcreate-add-service">
                  <Plus className="w-3.5 h-3.5 me-1" />
                  {t('إضافة خدمة', 'Add service')}
                </Button>
              </div>
              {serviceTypes.length === 0 && (
                <div className="p-2.5 rounded-lg bg-blue-500/5 border border-blue-500/20 text-blue-200/80 text-[11px] mb-3 flex items-start gap-2">
                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <p>
                    {t('لا توجد أنواع خدمة معرَّفة لهذه المنصة. أضِفها من: إعدادات القسم المالي → القوائم المنسدلة → قوائم إنشاء الفاتورة.',
                       'No Service Types defined for this platform. Add them at: Finance Settings → Dropdown Lists → Invoice Dropdowns.')}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {services.map((s, idx) => (
                  <ServiceRow
                    key={idx}
                    idx={idx}
                    row={s}
                    serviceTypes={serviceTypes}
                    serviceCatalog={serviceCatalog}
                    useCatalog={!!INVOICE_CATALOG_PLATFORM_MAP[platform] && serviceCatalog.length > 0}
                    onChange={(patch) => updateService(idx, patch)}
                    onRemove={() => removeService(idx)}
                    canRemove={services.length > 1}
                    t={t} isAr={isAr}
                  />
                ))}
              </div>
            </section>

            {/* ─── 4. Totals ─── */}
            <section className="p-4 rounded-2xl bg-white/[0.02] border border-white/10 space-y-2">
              <RowLine label={t('المبلغ قبل الضريبة', 'Subtotal')} value={<DirhamPrice value={subtotal} decimals={2} size="sm" />} />
              {tax.enabled ? (
                <RowLine label={t(`الضريبة المضافة (${tax.tax_percent}%)`, `VAT (${tax.tax_percent}%)`)}
                         value={<><span>+&nbsp;</span><DirhamPrice value={taxAmount} decimals={2} size="sm" /></>} muted />
              ) : (
                <div className="text-[11px] text-white/40">
                  {t('* الضريبة معطّلة من التحكم بالضريبة — لن تُضاف',
                     '* Tax is disabled in Tax Control — not applied')}
                </div>
              )}
              {commissionCfg && (
                <RowLine
                  label={t(`عمولة ${form.payment_method === 'tabby' ? 'تابي' : 'تمارا'} (داخلية — لا تظهر للعميل)`,
                           `${form.payment_method === 'tabby' ? 'Tabby' : 'Tamara'} commission (internal — hidden on customer PDF)`)}
                  value={<><span>+&nbsp;</span><DirhamPrice value={commissionCfg.amount} decimals={2} size="sm" /></>}
                  muted
                />
              )}
              <div className="border-t border-white/10 pt-2 flex items-center justify-between">
                <span className="text-white font-bold">{t('الإجمالي', 'Total')}</span>
                <span className="text-2xl font-bold text-emerald-400"><DirhamPrice value={total} decimals={2} size="sm" /></span>
              </div>
            </section>

            {/* ─── 5. Payment status (manual methods only) ─── */}
            {isManualMethod && (
              <PaymentStatusSection
                status={form.initial_payment_status}
                onStatusChange={(s) => setForm(f => ({ ...f, initial_payment_status: s }))}
                total={total}
                initialPayment={initialPayment}
                setInitialPayment={setInitialPayment}
                remainingAfterFirst={remainingAfterFirst}
                paymentMethod={form.payment_method}
                t={t} isAr={isAr}
              />
            )}

            {/* iter-278 — Paid invoice receipts (AI-verified against total) */}
            {form.initial_payment_status === 'paid_invoice' && (
              <PaidInvoiceReceiptsSection
                receipts={form.paid_receipts || []}
                onChange={(next) => setForm(f => ({ ...f, paid_receipts: next }))}
                declaredTotal={total}
                sum={paidReceiptsSum}
                allValid={paidReceiptsValid}
                t={t} isAr={isAr}
              />
            )}

            {/* ─── 6. Notes ─── */}
            <Field label={t('٦) ملاحظات', '6) Notes')}>
              <Input value={form.notes}
                     onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                     className="bg-[#050505] border-white/10 text-white"
                     placeholder={t('اختياري', 'Optional')}
                     data-testid="invcreate-notes" />
            </Field>

            {/* ─── 7. Supporting attachments (internal only) ───
                 iter-224 (Feb 2026) — At user's request. Files uploaded here
                 are NOT surfaced on the customer PDF/link — they are kept on
                 the invoice record for internal reference only. */}
            <SupportingAttachmentsSection
              attachments={form.supporting_attachments}
              onAdd={(rec) => setForm(f => ({
                ...f,
                supporting_attachments: [...(f.supporting_attachments || []), rec],
              }))}
              onRemove={(idx) => setForm(f => ({
                ...f,
                supporting_attachments: (f.supporting_attachments || []).filter((_, i) => i !== idx),
              }))}
              t={t} isAr={isAr}
            />

            {/* ─── Submit ─── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <Button variant="outline"
                      onClick={() => navigate(invoiceListRoute)}
                      className="border-white/10 bg-transparent text-white/80 hover:bg-white/5 h-12"
                      data-testid="invcreate-cancel">
                <ArrowLeft className="w-4 h-4 me-2" />
                {t('إلغاء والعودة', 'Cancel & back')}
              </Button>
              <Button onClick={submit} disabled={!canSubmit || submitting}
                      className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold h-12 disabled:opacity-50"
                      data-testid="invcreate-submit">
                {submitting
                  ? <><Loader2 className="w-4 h-4 animate-spin me-2" />{t('جارٍ الإنشاء...', 'Creating...')}</>
                  : <><Plus className="w-4 h-4 me-2" />{t('إنشاء الفاتورة', 'Create invoice')}</>}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Reusable subcomponents
// ─────────────────────────────────────────────────────────────────

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-white/70 text-xs mb-1.5">{label}</label>
      {children}
      {error && <div className="text-[11px] text-red-400 mt-1">{error}</div>}
    </div>
  );
}


function RowLine({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={muted ? 'text-white/50' : 'text-white/80'}>{label}</span>
      <span className={`font-mono ${muted ? 'text-white/60' : 'text-white'}`}>{value}</span>
    </div>
  );
}


function ServiceRow({ idx, row, serviceTypes, serviceCatalog, useCatalog, onChange, onRemove, canRemove, t, isAr }) {
  const isOther = row.service_type_id === OTHER_SERVICE_ID;

  const pickType = (id) => {
    if (id === OTHER_SERVICE_ID) {
      onChange({ service_type_id: OTHER_SERVICE_ID, service_type_ar: '', service_type_en: '' });
      return;
    }
    const st = serviceTypes.find(x => x.id === id);
    if (st) {
      onChange({
        service_type_id: id,
        service_type_ar: st.label_ar,
        service_type_en: st.label_en,
      });
    } else {
      onChange({ service_type_id: '', service_type_ar: '', service_type_en: '' });
    }
  };

  // iter-218n — Catalog picker: pick a MAIN service → subs auto-populated
  // → pick a SUB → service_type_ar/en persist as "Main — Sub".
  const pickMain = (mid) => {
    const m = serviceCatalog.find(x => x.id === mid);
    if (!m) {
      onChange({
        service_main_id: '', service_sub_id: '',
        service_type_id: '', service_type_ar: '', service_type_en: '',
      });
      return;
    }
    onChange({
      service_main_id: mid,
      service_sub_id: '',
      service_type_id: '',
      // Don't persist "main only" as a valid service_type yet — we wait
      // for the admin to pick a sub-service too. This keeps canSubmit
      // strict per user's requirement of "main + sub".
      service_type_ar: '',
      service_type_en: '',
    });
  };
  const pickSub = (sid) => {
    const m = serviceCatalog.find(x => x.id === row.service_main_id);
    if (!m) return;
    const s = (m.sub_services || []).find(x => x.id === sid);
    if (!s) {
      onChange({ service_sub_id: '', service_type_ar: '', service_type_en: '' });
      return;
    }
    onChange({
      service_sub_id: sid,
      service_type_ar: `${m.name_ar || ''} — ${s.name_ar || ''}`.trim(),
      service_type_en: `${m.name_en || m.name_ar || ''} — ${s.name_en || s.name_ar || ''}`.trim(),
    });
  };
  const currentMain = serviceCatalog.find(x => x.id === row.service_main_id);
  const availableSubs = currentMain?.sub_services || [];

  return (
    <div className="p-3 rounded-xl bg-white/[0.02] border border-white/10 space-y-2"
         data-testid={`invcreate-service-row-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="text-white/50 text-xs">{t(`خدمة ${idx + 1}`, `Service ${idx + 1}`)}</span>
        {canRemove && (
          <button onClick={onRemove}
                  className="p-1 rounded hover:bg-red-500/10 text-red-400/60"
                  data-testid={`invcreate-service-remove-${idx}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {useCatalog && serviceCatalog.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label className="block text-white/70 text-[11px] mb-1">{t('الخدمة الرئيسية', 'Main service') + ' *'}</label>
            <select
              value={row.service_main_id || ''}
              onChange={(e) => pickMain(e.target.value)}
              dir={isAr ? 'rtl' : 'ltr'}
              className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 px-2 text-sm focus:outline-none focus:border-[#d4af37]/60"
              data-testid={`invcreate-service-main-${idx}`}
            >
              <option value="">{t('— اختر الخدمة الرئيسية —', '— Pick a main service —')}</option>
              {serviceCatalog.map(m => (
                <option key={m.id} value={m.id}>{isAr ? m.name_ar : (m.name_en || m.name_ar)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-white/70 text-[11px] mb-1">{t('الخدمة الفرعية', 'Sub service') + ' *'}</label>
            <select
              value={row.service_sub_id || ''}
              onChange={(e) => pickSub(e.target.value)}
              disabled={!currentMain}
              dir={isAr ? 'rtl' : 'ltr'}
              className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 px-2 text-sm focus:outline-none focus:border-[#d4af37]/60 disabled:opacity-40"
              data-testid={`invcreate-service-sub-${idx}`}
            >
              <option value="">{currentMain ? t('— اختر الخدمة الفرعية —', '— Pick a sub service —') : t('اختر الخدمة الرئيسية أولاً', 'Pick a main service first')}</option>
              {availableSubs.map(s => (
                <option key={s.id} value={s.id}>{isAr ? s.name_ar : (s.name_en || s.name_ar)}</option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div>
          <label className="block text-white/70 text-[11px] mb-1">{t('نوع الخدمة', 'Service type') + ' *'}</label>
          <select
            value={row.service_type_id}
            onChange={(e) => pickType(e.target.value)}
            dir={isAr ? 'rtl' : 'ltr'}
            className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 px-2 text-sm focus:outline-none focus:border-[#d4af37]/60"
            data-testid={`invcreate-service-type-${idx}`}
          >
            <option value="">{t('— اختر نوع الخدمة —', '— Choose a service type —')}</option>
            {serviceTypes.map(st => (
              <option key={st.id} value={st.id}>{isAr ? st.label_ar : st.label_en}</option>
            ))}
            <option value={OTHER_SERVICE_ID}>{t('أخرى — اكتب يدوياً', 'Other — type manually')}</option>
          </select>
          {isOther && (
            <input
              type="text"
              value={row.service_type_ar}
              onChange={(e) => onChange({ service_type_ar: e.target.value, service_type_en: e.target.value })}
              placeholder={t('اكتب اسم الخدمة', 'Type the service name')}
              className="mt-2 w-full bg-[#050505] border border-[#d4af37]/40 text-white rounded-md h-10 px-3 text-sm focus:outline-none focus:border-[#d4af37]"
              dir={isAr ? 'rtl' : 'ltr'}
              data-testid={`invcreate-service-type-manual-${idx}`}
            />
          )}
        </div>
      )}

      <Field label={t('الوصف', 'Description')}>
        <Input value={row.description}
               onChange={(e) => onChange({ description: e.target.value })}
               className="bg-[#050505] border-white/10 text-white" />
      </Field>

      <Field label={t('المبلغ *', 'Amount *')}>
        <Input type="number" step="0.01" min="0" value={row.amount}
               onChange={(e) => onChange({ amount: e.target.value })}
               className="bg-[#050505] border-white/10 text-white" dir="ltr"
               data-testid={`invcreate-service-amount-${idx}`} />
      </Field>
    </div>
  );
}


function CommissionEditor({ cfg, provider, overrideValue, onOverrideChange, subtotal, t, isAr }) {
  const [showOverride, setShowOverride] = useState(false);
  const isPct = cfg.type === 'percentage';
  const unit = isPct ? '%' : 'AED';
  const providerName = provider === 'tabby' ? t('تابي', 'Tabby') : t('تمارا', 'Tamara');

  return (
    <div className="mt-3 p-4 rounded-xl bg-[#d4af37]/5 border border-[#d4af37]/20">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[#d4af37]" />
        <h4 className="text-sm text-white font-medium">
          {t(`عمولة ${providerName} — ${isPct ? 'نسبة' : 'مبلغ ثابت'}`,
             `${providerName} commission — ${isPct ? 'percentage' : 'fixed amount'}`)}
        </h4>
      </div>
      <p className="text-xs text-white/60 mb-3">
        {t(`الحد الأدنى من الإعدادات: ${cfg.min}${isPct ? '%' : ''}. لا يمكن اختيار قيمة أقل.`,
           `Minimum from settings: ${cfg.min}${isPct ? '%' : ''}. Cannot go below.`)}
      </p>

      <label className="inline-flex items-center gap-2 text-white/80 text-xs mb-2 cursor-pointer">
        <input type="checkbox" checked={showOverride}
               onChange={(e) => {
                 setShowOverride(e.target.checked);
                 if (!e.target.checked) onOverrideChange('');
               }}
               className="accent-[#d4af37]"
               data-testid={`invcreate-commission-override-toggle-${provider}`} />
        {t('تعديل قيمة العمولة لهذه الفاتورة (اختياري)',
           'Adjust the commission for this invoice (optional)')}
      </label>

      {showOverride ? (
        <div className="relative">
          <Input
            type="number" step="0.01" min={cfg.min}
            value={overrideValue}
            onChange={(e) => onOverrideChange(e.target.value)}
            placeholder={String(cfg.min)}
            className={`bg-[#050505] text-white pe-12 ${cfg.belowMin ? 'border-red-500/60' : 'border-white/10'}`}
            dir="ltr"
            data-testid={`invcreate-commission-override-input-${provider}`}
          />
          <span className="absolute inset-y-0 end-3 flex items-center text-white/40 text-xs">{unit}</span>
        </div>
      ) : (
        <div className="text-xs text-white/70">
          <span className="text-white/50">{t('القيمة المستخدمة:', 'Using value:')} </span>
          <span className="font-mono text-white">{cfg.min}{isPct ? '%' : ''}</span>
        </div>
      )}

      {cfg.belowMin && (
        <div className="mt-2 text-[11px] text-red-400">
          {t(`لا يمكن أن تقل عن الحد الأدنى (${cfg.min}${isPct ? '%' : ''})`,
             `Cannot be lower than the settings minimum (${cfg.min}${isPct ? '%' : ''})`)}
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-white/5 text-xs flex items-center justify-between">
        <span className="text-white/50">
          {t('العمولة المضافة (تُخفى عن العميل)', 'Commission added (hidden from customer)')}
        </span>
        <span className="font-mono text-[#d4af37]">
          +<DirhamPrice value={cfg.amount} decimals={2} size="sm" />
        </span>
      </div>
    </div>
  );
}


function PaymentStatusSection({ status, onStatusChange, total, initialPayment, setInitialPayment,
                                remainingAfterFirst, paymentMethod, t, isAr }) {
  // iter-218n (Feb 2026) — Status buttons moved up to section 2 next to
  // the customer info. This section is now purely the payment-DETAIL
  // block (amount, proof, method-note) shown when status !== unpaid.
  if (status === 'unpaid') return null;
  return (
    <section>
      <h3 className="text-white font-bold text-sm mb-3">{t('٥) تفاصيل الدفعة الأولى', '5) Initial payment details') + ' *'}</h3>
      <InitialPaymentBlock
        status={status}
        total={total}
        data={initialPayment}
        onChange={setInitialPayment}
        remainingAfterFirst={remainingAfterFirst}
        paymentMethod={paymentMethod}
        t={t} isAr={isAr}
      />
    </section>
  );
}


const PAY_METHODS_FOR_PARTIAL = [
  { id: 'cash',          ar: 'نقدي',         en: 'Cash' },
  { id: 'bank_transfer', ar: 'تحويل بنكي',   en: 'Bank Transfer' },
  { id: 'check',         ar: 'شيك',          en: 'Check' },
  { id: 'credit_card',   ar: 'بطاقة ائتمان', en: 'Credit Card' },
  { id: 'other',         ar: 'أخرى',         en: 'Other' },
];

function InitialPaymentBlock({ status, total, data, onChange, remainingAfterFirst,
                               paymentMethod, t, isAr }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [ocring, setOcring] = useState(false);
  const [ocrHint, setOcrHint] = useState(null);

  const isPaid = status === 'paid';
  const amtLocked = isPaid;  // for "paid" the amount must equal the total

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setOcrHint(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_URL}/api/upload/file`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const rec = r.data || {};
      onChange(p => ({
        ...p,
        proof: { file_id: rec.file_id || rec.id, filename: rec.filename || file.name },
      }));
      toast.success(t('تم رفع الوصل', 'Receipt uploaded'));

      // Best-effort OCR — never block on failure
      if ((file.type || '').startsWith('image/')) {
        setOcring(true);
        try {
          const ocr = await axios.post(`${API_URL}/api/admin/invoices/ocr-receipt`,
            { file_id: rec.file_id || rec.id },
            { headers: getAuthHeaders() });
          const d = ocr.data || {};
          setOcrHint(d);
          // Autofill amount if we don't have one yet and status is partial (paid is locked)
          if (!isPaid && !data.amount && d.amount) {
            onChange(p => ({ ...p, amount: String(d.amount) }));
          }
          // Autofill note with reference if empty
          if (!data.note && d.reference) {
            onChange(p => ({ ...p, note: `Ref: ${d.reference}` }));
          }
        } catch { /* silent */ }
        finally { setOcring(false); }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل رفع الوصل', 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-3">
      <div className="text-xs text-white/70">
        {isPaid
          ? t(<>الحالة: مدفوعة كامل. المبلغ المستحق: <DirhamPrice value={total} decimals={2} size="sm" /> — إجباري رفع وصل</>,
               <>Status: Fully paid. Amount due: <DirhamPrice value={total} decimals={2} size="sm" /> — receipt is required</>)
          : t(<>الحالة: مدفوعة جزئياً. أدخل مبلغ الدفعة الأولى وارفع الوصل (المتبقي: <DirhamPrice value={remainingAfterFirst} decimals={2} size="sm" />)</>,
               <>Status: Partially paid. Enter first-payment amount and upload receipt (remaining: <DirhamPrice value={remainingAfterFirst} decimals={2} size="sm" />)</>)}
      </div>

      {status === 'partially_paid' && (
        <div>
          <label className="block text-white/70 text-xs mb-1.5">{t('نوع الدفعة', 'Payment method')}</label>
          <select
            value={data.method}
            onChange={(e) => onChange(p => ({ ...p, method: e.target.value }))}
            dir={isAr ? 'rtl' : 'ltr'}
            className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 px-2 text-sm"
            data-testid="invcreate-initial-method"
          >
            {PAY_METHODS_FOR_PARTIAL.map(m => (
              <option key={m.id} value={m.id}>{isAr ? m.ar : m.en}</option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="block text-white/70 text-xs mb-1.5">
          {t('المبلغ المدفوع', 'Paid amount') + ' *'}
        </label>
        <div className="flex items-center gap-2">
          <Input
            type="number" step="0.01" min="0"
            value={data.amount}
            disabled={amtLocked}
            onChange={(e) => onChange(p => ({ ...p, amount: e.target.value }))}
            className="bg-[#050505] border-white/10 text-white disabled:opacity-60"
            dir="ltr"
            data-testid="invcreate-initial-amount"
          />
          {status === 'partially_paid' && (
            <Button
              type="button" size="sm" variant="outline"
              className="border-white/10 bg-transparent text-white/70 hover:bg-white/5"
              onClick={() => onChange(p => ({ ...p, amount: String(floorAED(total / 2).toFixed(2)) }))}
              data-testid="invcreate-initial-halffill"
            >
              {t('نصف الإجمالي', 'Half of total')}
            </Button>
          )}
        </div>
        {amtLocked && (
          <div className="mt-1 text-[11px] text-white/40 flex items-center gap-1">
            <Lock className="w-3 h-3" /> {t('مقفل على المبلغ الكامل', 'Locked to full total')}
          </div>
        )}
      </div>

      <div>
        <label className="block text-white/70 text-xs mb-1.5">
          {t('وصل / إيصال الدفع (صورة أو PDF)', 'Payment receipt (image or PDF)') + ' *'}
        </label>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="invcreate-initial-proof-file"
        />
        {data.proof ? (
          <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/30 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-emerald-200 flex-1">{data.proof.filename}</span>
            <button
              onClick={() => onChange(p => ({ ...p, proof: null }))}
              className="text-xs text-red-400/80 hover:text-red-400"
              data-testid="invcreate-initial-proof-remove"
            >
              {t('حذف', 'Remove')}
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-full p-4 rounded-lg border border-dashed border-white/20 hover:border-[#d4af37]/40 hover:bg-white/[0.02] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="invcreate-initial-proof-btn"
          >
            {uploading
              ? <><Loader2 className="w-4 h-4 animate-spin text-white/60" /><span className="text-sm text-white/60">{t('جارٍ الرفع…', 'Uploading…')}</span></>
              : <><Upload className="w-4 h-4 text-white/60" /><span className="text-sm text-white/60">{t('اضغط لرفع الوصل', 'Click to upload the receipt')}</span></>}
          </button>
        )}

        {ocring && (
          <div className="mt-2 text-[11px] text-white/50 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" />
            {t('جارٍ استخراج البيانات من الوصل بواسطة Gemini…', 'Gemini is extracting details from the receipt…')}
          </div>
        )}
        {ocrHint && (ocrHint.amount || ocrHint.reference || ocrHint.date) && (
          <div className="mt-2 p-2.5 rounded-md bg-blue-500/5 border border-blue-500/20 text-[11px] text-blue-200/80 flex items-start gap-2">
            <Sparkles className="w-3.5 h-3.5 text-blue-300 mt-0.5" />
            <div>
              <div className="mb-0.5">{t('استخراج تلقائي من الوصل:', 'Auto-extracted from the receipt:')}</div>
              <ul className="ms-4 list-disc">
                {ocrHint.amount != null && <li>{t('المبلغ', 'Amount')}: {ocrHint.amount} {ocrHint.currency || 'AED'}</li>}
                {ocrHint.date && <li>{t('التاريخ', 'Date')}: {ocrHint.date}</li>}
                {ocrHint.reference && <li>{t('المرجع', 'Reference')}: <span dir="ltr" className="font-mono">{ocrHint.reference}</span></li>}
              </ul>
            </div>
          </div>
        )}
      </div>

      <Field label={t('ملاحظة على الدفعة (اختياري)', 'Payment note (optional)')}>
        <Input
          value={data.note}
          onChange={(e) => onChange(p => ({ ...p, note: e.target.value }))}
          className="bg-[#050505] border-white/10 text-white"
          data-testid="invcreate-initial-note"
        />
      </Field>
    </div>
  );
}


function ResultView({ result, copyLink, onCreateAnother, onDone, t, isAr }) {
  const err = result.gateway_error;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 space-y-5"
    >
      <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3">
        <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0" />
        <div>
          <div className="text-emerald-300 font-bold">{t('تم إنشاء الفاتورة', 'Invoice created')} — {result.invoice_no_display || `INV-${result.invoice_no}`}</div>
          <div className="text-emerald-200/80 text-xs">
            {t('الإجمالي', 'Total')}: <DirhamPrice value={result.total} decimals={2} size="sm" /> · {result.customer?.name}
            {result.status && <span className="ms-2">· {t('الحالة', 'Status')}: {result.status}</span>}
          </div>
        </div>
      </div>

      {err && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs">
          {t('تعذّر إنشاء رابط الدفع', 'Payment link creation failed')}: {String(err)}
        </div>
      )}

      {result.checkout_url && (
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <p className="text-xs text-white/50 mb-2">{t('رابط الدفع الذي سيُرسل للعميل', 'Payment link for the customer')}</p>
          <p className="text-white text-xs break-all font-mono mb-3" dir="ltr">{result.checkout_url}</p>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={copyLink} className="bg-white/10 hover:bg-white/15 text-white" data-testid="invcreate-result-copy">
              <Copy className="w-4 h-4 me-1.5" />
              {t('نسخ', 'Copy')}
            </Button>
            <a href={result.checkout_url} target="_blank" rel="noreferrer">
              <Button className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black w-full" data-testid="invcreate-result-open">
                <ExternalLink className="w-4 h-4 me-1.5" />
                {t('فتح', 'Open')}
              </Button>
            </a>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Button variant="outline" onClick={onCreateAnother}
                className="border-white/10 bg-transparent text-white/80 hover:bg-white/5 h-11"
                data-testid="invcreate-result-another">
          <Plus className="w-4 h-4 me-2" />
          {t('إنشاء فاتورة أخرى', 'Create another invoice')}
        </Button>
        <Button onClick={onDone}
                className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold h-11"
                data-testid="invcreate-result-done">
          <ListChecks className="w-4 h-4 me-2" />
          {t('العودة إلى قائمة الفواتير', 'Back to invoice list')}
        </Button>
      </div>
    </motion.div>
  );
}



// iter-224 (Feb 2026) — Supporting attachments block.
// Uploads through the existing /api/upload/file endpoint so we reuse the
// same storage the initial-payment proof does. Files here are INTERNAL —
// they are never sent on the customer PDF or the payment-link page.
function SupportingAttachmentsSection({ attachments, onAdd, onRemove, t, isAr }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const MAX_FILES = 10;

  const pick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    handleFiles(files);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFiles = async (files) => {
    if ((attachments || []).length + files.length > MAX_FILES) {
      toast.error(t(`الحد الأقصى ${MAX_FILES} مرفقات`, `Max ${MAX_FILES} attachments`));
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          const r = await axios.post(`${API_URL}/api/upload/file`, fd, {
            headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          });
          const rec = r.data || {};
          onAdd({
            file_id: rec.file_id || rec.id,
            filename: rec.filename || file.name,
            size: file.size,
            uploaded_at: new Date().toISOString(),
          });
        } catch (e) {
          toast.error(e.response?.data?.detail || t(`فشل رفع ${file.name}`, `Failed to upload ${file.name}`));
        }
      }
      toast.success(t('تم رفع المرفقات', 'Attachments uploaded'));
    } finally {
      setUploading(false);
    }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <section data-testid="invcreate-supporting-attachments">
      <h3 className="text-white font-bold text-sm mb-1">
        {t('٧) مرفقات داعمة', '7) Supporting attachments')}
        <span className="text-white/40 font-normal text-xs ms-2">
          {t('(اختياري — لا تُرسل للعميل)', '(optional — internal only)')}
        </span>
      </h3>
      <p className="text-[11px] text-white/50 mb-3">
        {t('ملفات تُحفظ مع الفاتورة للرجوع الداخلي فقط (عقود، طلبات شراء، صور مرجعية…).',
           'Files kept with the invoice for internal reference only (contracts, POs, reference images…).')}
      </p>

      <div className="rounded-xl border border-white/10 bg-[#050505]/60 p-3">
        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          onChange={pick}
          data-testid="invcreate-attach-input"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || (attachments || []).length >= MAX_FILES}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed border-white/15 hover:border-white/30 text-white/70 hover:text-white text-sm transition disabled:opacity-50"
          data-testid="invcreate-attach-add"
        >
          {uploading
            ? <><Loader2 className="w-4 h-4 animate-spin" />{t('جارٍ الرفع…', 'Uploading…')}</>
            : <><Upload className="w-4 h-4" />{t('إضافة مرفقات', 'Add attachments')}</>}
        </button>

        {(attachments || []).length > 0 && (
          <ul className="mt-3 space-y-1.5" data-testid="invcreate-attach-list">
            {attachments.map((a, idx) => (
              <li
                key={a.file_id || idx}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/5"
                data-testid={`invcreate-attach-item-${idx}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FileImage className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-white/90 text-xs truncate" title={a.filename}>{a.filename}</span>
                  {a.size ? <span className="text-white/40 text-[10px] flex-shrink-0">{fmtSize(a.size)}</span> : null}
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  className="p-1 rounded hover:bg-red-500/10 text-red-400/70 hover:text-red-400 flex-shrink-0"
                  aria-label="remove"
                  data-testid={`invcreate-attach-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}



// iter-278 (Feb 2026) — Paid-invoice receipts uploader with AI validation.
// Each row: file upload → auto-OCR (best-effort autofill) → manual amount confirm.
// Once >=1 row exists, an "AI Verify All" button calls
// /api/admin/invoices/verify-receipts to confirm the sum >= total. All rows
// get flagged `ai_verified` on success — otherwise the submit button stays disabled.
function PaidInvoiceReceiptsSection({ receipts, onChange, declaredTotal, sum, allValid, t, isAr }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const remaining = floorAED((declaredTotal || 0) - (sum || 0));

  const addFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    const nextRows = [...(receipts || [])];
    try {
      for (const file of list) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          const r = await axios.post(`${API_URL}/api/upload/file`, fd, {
            headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          });
          const rec = r.data || {};
          const file_id = rec.file_id || rec.id;
          let amount = 0;
          let reference = '';
          // Best-effort autofill via existing single-file OCR
          if ((file.type || '').startsWith('image/')) {
            try {
              const ocr = await axios.post(`${API_URL}/api/admin/invoices/ocr-receipt`,
                { file_id },
                { headers: getAuthHeaders() });
              const d = ocr.data || {};
              if (d.amount) amount = Number(d.amount) || 0;
              if (d.reference) reference = String(d.reference);
            } catch { /* silent */ }
          }
          nextRows.push({
            file_id,
            filename: rec.filename || file.name,
            amount: amount || '',
            method: 'bank_transfer',
            reference,
            ai_verified: false,
            note: '',
          });
        } catch (e) {
          toast.error(e.response?.data?.detail || t(`فشل رفع ${file.name}`, `Failed to upload ${file.name}`));
        }
      }
      onChange(nextRows);
      // Reset verify result on any change
      setVerifyResult(null);
      toast.success(t('تم رفع الإيصالات', 'Receipts uploaded'));
    } finally {
      setUploading(false);
    }
  };

  const removeRow = (idx) => {
    onChange((receipts || []).filter((_, i) => i !== idx));
    setVerifyResult(null);
  };

  const updateRow = (idx, patch) => {
    onChange((receipts || []).map((r, i) => i === idx ? { ...r, ...patch, ai_verified: false } : r));
    setVerifyResult(null);
  };

  const verifyAll = async () => {
    const list = receipts || [];
    if (!list.length) return;
    if (list.some(r => !r.file_id || !parseFloat(r.amount))) {
      toast.error(t('أدخل مبلغ صحيح لكل إيصال', 'Enter a valid amount for each receipt'));
      return;
    }
    setVerifying(true);
    setVerifyResult(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/invoices/verify-receipts`,
        {
          file_ids: list.map(x => x.file_id),
          declared_total: declaredTotal,
        },
        { headers: getAuthHeaders() }
      );
      const d = r.data || {};
      setVerifyResult(d);
      // Mark all rows as ai_verified on OK
      if (d.ok) {
        onChange(list.map(x => ({ ...x, ai_verified: true })));
        toast.success(isAr ? d.reason_ar : d.reason_en);
      } else {
        toast.error(isAr ? d.reason_ar : d.reason_en);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('فشل التحقق', 'Verification failed'));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <section data-testid="invcreate-paid-invoice-section">
      <h3 className="text-white font-bold text-sm mb-2">
        {t('٥) إيصالات الفاتورة المدفوعة (تحقق بالذكاء الاصطناعي)',
           '5) Paid-invoice receipts (AI verification)') + ' *'}
      </h3>
      <p className="text-[11px] text-white/50 mb-3">
        {t('ارفع إيصال أو أكثر لإثبات الدفع الكامل. سيتحقق الذكاء الاصطناعي من مطابقة المبلغ الإجمالي للإيصالات مع إجمالي الفاتورة قبل الحفظ.',
           'Upload one or more receipts proving full payment. AI will verify the sum matches the invoice total before saving.')}
      </p>

      <div className="rounded-xl border border-white/10 bg-[#050505]/60 p-3 space-y-3">
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
          data-testid="invcreate-paid-receipts-input"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed border-purple-500/30 hover:border-purple-500/60 text-purple-200 hover:text-white text-sm transition disabled:opacity-50"
          data-testid="invcreate-paid-receipts-add-btn"
        >
          {uploading
            ? <><Loader2 className="w-4 h-4 animate-spin" />{t('جارٍ الرفع…', 'Uploading…')}</>
            : <><Upload className="w-4 h-4" />{t('إضافة إيصال / إيصالات', 'Add receipt(s)')}</>}
        </button>

        {(receipts || []).length > 0 && (
          <ul className="space-y-2" data-testid="invcreate-paid-receipts-list">
            {receipts.map((r, idx) => (
              <li
                key={r.file_id || idx}
                className={`p-3 rounded-lg border ${r.ai_verified ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-white/[0.02] border-white/10'}`}
                data-testid={`invcreate-paid-receipt-row-${idx}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <FileImage className={`w-4 h-4 ${r.ai_verified ? 'text-emerald-400' : 'text-white/60'} flex-shrink-0`} />
                  <span className="text-xs text-white/90 flex-1 truncate" title={r.filename}>{r.filename}</span>
                  {r.ai_verified && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {t('تم التحقق', 'Verified')}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="p-1 rounded hover:bg-red-500/10 text-red-400/70 hover:text-red-400"
                    data-testid={`invcreate-paid-receipt-remove-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div>
                    <label className="text-[10px] text-white/50 mb-1 block">{t('المبلغ (AED)', 'Amount (AED)') + ' *'}</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={r.amount}
                      onChange={(e) => updateRow(idx, { amount: e.target.value })}
                      className="bg-[#050505] border-white/10 text-white h-9"
                      dir="ltr"
                      data-testid={`invcreate-paid-receipt-amount-${idx}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-white/50 mb-1 block">{t('الطريقة', 'Method')}</label>
                    <select
                      value={r.method || 'bank_transfer'}
                      onChange={(e) => updateRow(idx, { method: e.target.value })}
                      className="w-full bg-[#050505] border border-white/10 text-white h-9 rounded-md px-2 text-sm"
                      data-testid={`invcreate-paid-receipt-method-${idx}`}
                    >
                      <option value="cash">{t('نقدي', 'Cash')}</option>
                      <option value="bank_transfer">{t('تحويل بنكي', 'Bank Transfer')}</option>
                      <option value="check">{t('شيك', 'Check')}</option>
                      <option value="credit_card">{t('بطاقة ائتمان', 'Credit Card')}</option>
                      <option value="other">{t('أخرى', 'Other')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-white/50 mb-1 block">{t('المرجع', 'Reference')}</label>
                    <Input
                      value={r.reference || ''}
                      onChange={(e) => updateRow(idx, { reference: e.target.value })}
                      className="bg-[#050505] border-white/10 text-white h-9"
                      dir="ltr"
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {(receipts || []).length > 0 && (
          <div className="pt-3 border-t border-white/10 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/70">{t('إجمالي الإيصالات', 'Receipts sum')}</span>
              <span className="font-mono text-white">
                <DirhamPrice value={sum || 0} decimals={2} size="sm" />
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/70">{t('إجمالي الفاتورة', 'Invoice total')}</span>
              <span className="font-mono text-white">
                <DirhamPrice value={declaredTotal || 0} decimals={2} size="sm" />
              </span>
            </div>
            <div className={`flex items-center justify-between text-sm font-bold ${remaining <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              <span>{remaining <= 0 ? t('فائض / يغطي', 'Covers total') : t('المتبقّي', 'Remaining')}</span>
              <span className="font-mono">
                <DirhamPrice value={Math.abs(remaining)} decimals={2} size="sm" />
              </span>
            </div>

            <Button
              type="button"
              onClick={verifyAll}
              disabled={verifying || (receipts || []).length === 0}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
              data-testid="invcreate-paid-receipts-verify-btn"
            >
              {verifying
                ? <><Loader2 className="w-4 h-4 animate-spin me-2" />{t('جارٍ التحقق بالذكاء الاصطناعي…', 'AI verifying…')}</>
                : <><Sparkles className="w-4 h-4 me-2" />{t('تحقق بالذكاء الاصطناعي', 'AI Verify Receipts')}</>}
            </Button>

            {verifyResult && (
              <div className={`p-3 rounded-lg text-xs border ${verifyResult.ok
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
                : 'bg-red-500/10 border-red-500/30 text-red-200'}`}
                data-testid="invcreate-paid-receipts-verify-result"
              >
                <div className="flex items-center gap-2 mb-1">
                  {verifyResult.ok
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    : <Info className="w-4 h-4 text-red-400" />}
                  <span className="font-medium">{isAr ? verifyResult.reason_ar : verifyResult.reason_en}</span>
                </div>
                <div className="text-white/60 text-[11px]">
                  {t('المكتشف', 'Detected')}: {verifyResult.sum_detected} AED · {t('المُعلَن', 'Declared')}: {verifyResult.declared_total} AED
                </div>
                {Array.isArray(verifyResult.per_file) && verifyResult.per_file.length > 0 && (
                  <ul className="mt-2 ms-4 list-disc space-y-0.5">
                    {verifyResult.per_file.map((pf, i) => (
                      <li key={pf.file_id || i} className="text-[11px]">
                        {pf.amount != null
                          ? `${pf.amount} ${pf.currency || 'AED'}${pf.reference ? ` · ${pf.reference}` : ''}`
                          : (pf.error || t('لم يُكتشف مبلغ', 'No amount detected'))}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {!allValid && (receipts || []).some(r => !r.ai_verified) && (
              <div className="text-[11px] text-amber-300 flex items-center gap-1">
                <Info className="w-3 h-3" />
                {t('يجب تشغيل التحقق بالذكاء الاصطناعي قبل الحفظ',
                   'You must run AI verification before saving')}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
